import { getAuthHeaders } from './apiClient';
import { getApiBaseUrl } from '../config/apiBase';
import { trackEvent } from './analyticsService';
import type { FoodRecognitionResponse } from '../types/foodRecognition';
import type { ScanReview } from '../utils/receiptScanReview';

/**
 * Sends a receipt scan's evidence, then the cook's verdict on it, to
 * `/api/receipt-scan` (see `api/_lib/receiptScanStore.ts` for what is kept).
 *
 * Two rules:
 *  - **It never gets in the cook's way.** Nothing here throws and nothing is
 *    awaited by the screen. A scan that cannot be recorded is still a scan.
 *  - **It never fails in silence.** A record that did not land is reported as
 *    `receipt_scan_record_failed`, because a loop that quietly stops recording
 *    looks exactly like a scanner nobody corrects.
 */

export interface RecordedScan {
  scanId: string;
  at: string;
}

let lastScan: RecordedScan | null = null;

/** The scan on screen, or the last one, for the bug reporter to quote. */
export const getLastReceiptScan = (): RecordedScan | null => lastScan;

/** Test seam. */
export const resetReceiptScanRecorder = (): void => { lastScan = null; };

export const newScanId = (now: number = Date.now()): string =>
  `scan_${now.toString(36)}_${Math.random().toString(36).slice(2, 10).padEnd(8, '0')}`;

type Stage = 'scanned' | 'reviewed';

const post = async (stage: Stage, body: Record<string, unknown>): Promise<boolean> => {
  try {
    const response = await fetch(`${getApiBaseUrl()}/api/receipt-scan`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify({ stage, ...body }),
    });
    // The Expo dev server answers 200 with HTML for a path it does not know, so
    // `ok` alone proves nothing: the body has to say the record landed.
    const answer = await response.json().catch(() => null);
    if (response.ok && answer?.ok === true) return true;
    trackEvent('receipt_scan_record_failed', { stage, status: response.status });
    console.warn(`receiptScanRecorder - ${stage} not recorded: HTTP ${response.status} ${answer?.error ?? ''}`);
    return false;
  } catch (error) {
    trackEvent('receipt_scan_record_failed', { stage, status: 0 });
    console.warn(
      `receiptScanRecorder - ${stage} not recorded:`,
      error instanceof Error ? error.message : String(error),
    );
    return false;
  }
};

export interface ScanPhoto {
  base64: string;
  mimeType: string;
}

/**
 * Record what the scan read. Called as soon as the result is on screen, so a
 * cook who shakes the phone before confirming still leaves a reproduction.
 */
export const recordScanned = (
  scanId: string,
  result: FoodRecognitionResponse,
  extras: { language?: string; appVersion?: string; photo?: Promise<ScanPhoto | null> } = {},
): Promise<boolean> => {
  lastScan = { scanId, at: new Date().toISOString() };
  return (async () => {
    const photo = extras.photo ? await extras.photo.catch(() => null) : null;
    return post('scanned', {
      scanId,
      store: result.receipt_metadata?.store_name,
      purchaseDate: result.receipt_metadata?.purchase_date,
      total: result.receipt_metadata?.total_amount,
      bandsOk: result.bands_ok,
      bandsTotal: result.bands_total,
      seamMerges: result.scan_evidence?.seam_merges,
      model: result.scan_evidence?.model,
      language: extras.language,
      appVersion: extras.appVersion,
      bands: result.scan_evidence?.band_reads ?? [],
      items: result.detected_items,
      warnings: result.validation_warnings ?? [],
      ...(photo ? { photo } : {}),
    });
  })();
};

/**
 * Record what the cook did with it. Waits for the first write: a review can
 * only be attached to a scan that exists, and the cook can confirm faster than
 * the photo uploads.
 */
export const recordReviewed = async (
  scanId: string,
  review: ScanReview,
  scanned?: Promise<boolean>,
): Promise<boolean> => {
  if (scanned && !(await scanned)) return false;
  return post('reviewed', { scanId, rows: review.rows });
};
